import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AggregateMinAvgMax,
  ParameterKey,
  Torch
} from '../../utils/weldParameterAggregator'
import { AggKey, VisibleColumns } from '../../utils/weldParametersColumnLayout'
import ParameterMatrix, { FilterApi, FilterTarget } from './ParameterMatrix'
import {
  applyWeldParameterFilters,
  buildFilterOptions,
  EMPTY_WP_FILTER,
  isFilterActive,
  readFilters,
  targetKey,
  writeFilters,
  type WeldParameterFilterRow,
  type WeldParameterFilterState,
  type WeldParameterFiltersMap
} from '../../utils/weldParametersFilters'
import { resolvePreheatCellsForPass } from '../../utils/preheatInterpassForPass'
import { applyPqrToleranceToTriple } from '../../utils/pqr/applyPqrTolerance'
import type { PqrLimitsMode } from '../../utils/pqr/applyPqrTolerance'
import {
  formatMatrixNumber,
  renderMatrixNumber
} from './weldParameters/weldParameterCellDisplay'
import { renderPassWithIwmSwapBadge } from './weldParameters/IwmRootSwappedBadge'
import { applyIwmRootSwapToManualRows } from '../../utils/pqr/applyIwmRootSwapToWeldParams'

export type ManualAgg = AggKey

/**
 * One persisted manual row per pass. `torch` is the single torch the user
 * selected via the in-row dropdown; mixed-torch uploaded passes default to
 * 'Lead' on mode switch and the user can change it freely afterwards.
 *
 * `values` is sparse — only cells the user has touched need be present.
 */
export interface ManualRow {
  pass: string
  torch: Torch
  values: Partial<Record<ParameterKey, Partial<AggregateMinAvgMax>>>
}

interface WeldParametersManualViewProps {
  rows: ManualRow[]
  passLabels: string[]
  /** Per-(param, agg) visibility. Defaults to the issue-03 live columns. */
  visibleColumns?: VisibleColumns
  onChange: (
    pass: string,
    param: ParameterKey,
    agg: ManualAgg,
    value: number | null
  ) => void
  onTorchChange: (pass: string, torch: Torch) => void
  /**
   * Optional opaque key used to scope filter persistence in `sessionStorage`.
   * The parent (`WeldParameters`) builds this from `(project, pipeSet, weld)`
   * so each weld keeps its own filter draft for the session. When omitted,
   * filters live in component state only.
   */
  filtersStorageKey?: string
  /**
   * When `true`, every value cell renders as text and the torch select is
   * locked. Used after a manual Save so the user lands back in a viewer
   * (not the edit form) until they explicitly re-enter edit mode via the
   * pencil. `onChange` / `onTorchChange` are not invoked while read-only.
   */
  readOnly?: boolean
  /**
   * Optional sticky leftmost actions column. Mirrors
   * `WeldParametersUploadedView.onPassView`: when supplied, the manual
   * matrix renders this for every pass row (typically the per-pass eye
   * icon). Disabled state is the caller's responsibility — useful when
   * a manual weld has no telemetry samples yet.
   */
  renderRowActions?: (pass: string) => React.ReactNode
  /** Pipe-set `sections.preheat` — read-only source for the Preheat / Interpass column. */
  preheatSection?: Record<string, unknown> | null
  preheatInterpassByPass?: Record<string, { min: string; max: string; avg?: string }> | null
  toleranceBoundary?: number
  pqrLimitsMode?: PqrLimitsMode
  /** When true, Root pass shows a Swapped badge (Excel Root CW↔CCW). */
  swapIwmBugType?: boolean
}

/**
 * Editable variant of the parameter matrix used when there's no TXT upload.
 * Layout chrome lives in `ParameterMatrix`; this component only supplies
 * the torch <select> and per-cell <input> renderers and wires their change
 * handlers back up to the parent.
 */
export default function WeldParametersManualView({
  rows,
  passLabels,
  visibleColumns,
  onChange,
  onTorchChange,
  filtersStorageKey,
  readOnly = false,
  renderRowActions,
  preheatSection,
  preheatInterpassByPass,
  toleranceBoundary = 0,
  pqrLimitsMode = 'minmax',
  swapIwmBugType = false
}: WeldParametersManualViewProps) {
  // When swap is on and the matrix is read-only, show Root (CW)/(CCW) values
  // exchanged to match reports. While editing, keep stored values.
  const displayRows = useMemo(
    () =>
      readOnly && swapIwmBugType
        ? applyIwmRootSwapToManualRows(rows, true)
        : rows,
    [rows, readOnly, swapIwmBugType]
  )
  const byPass = useMemo(() => indexRows(displayRows), [displayRows])
  const resolvedPreheatUnit =
    String(preheatSection?.preheatUnit ?? '°F').trim() || '°F'

  const filterRowsByPass = useMemo<Map<string, WeldParameterFilterRow>>(() => {
    const map = new Map<string, WeldParameterFilterRow>()
    for (const pass of passLabels) {
      const row = byPass.get(pass)
      map.set(pass, {
        pass,
        torch: row?.torch ?? null,
        values: row?.values ?? {}
      })
    }
    return map
  }, [byPass, passLabels])

  const filterRows = useMemo(
    () => Array.from(filterRowsByPass.values()),
    [filterRowsByPass]
  )

  const [filters, setFilters] = useState<WeldParameterFiltersMap>(() =>
    readFilters(filtersStorageKey ?? '')
  )
  useEffect(() => {
    setFilters(readFilters(filtersStorageKey ?? ''))
  }, [filtersStorageKey])

  const persistFilters = useCallback(
    (next: WeldParameterFiltersMap) => {
      writeFilters(filtersStorageKey ?? '', next)
    },
    [filtersStorageKey]
  )

  const updateFilter = useCallback(
    (target: FilterTarget, updater: (prev: WeldParameterFilterState) => WeldParameterFilterState) => {
      setFilters((prev) => {
        const key = targetKey(target)
        const current = prev[key] ?? EMPTY_WP_FILTER
        const nextEntry = updater(current)
        const next = { ...prev, [key]: nextEntry }
        if (!isFilterActive(nextEntry)) {
          delete next[key]
        }
        persistFilters(next)
        return next
      })
    },
    [persistFilters]
  )

  const filterApi: FilterApi = useMemo(
    () => ({
      getOptions: (target) => buildFilterOptions(filterRows, target),
      getValue: (target) => filters[targetKey(target)]?.values ?? null,
      getText: (target) => filters[targetKey(target)]?.text ?? '',
      onChange: (target, values) =>
        updateFilter(target, (prev) => ({ ...prev, values })),
      onTextChange: (target, text) =>
        updateFilter(target, (prev) => ({ ...prev, text }))
    }),
    [filterRows, filters, updateFilter]
  )

  const filteredPassLabels = useMemo(
    () => applyWeldParameterFilters(passLabels, filterRowsByPass, filters),
    [passLabels, filterRowsByPass, filters]
  )

  return (
    <ParameterMatrix
      testId="wp-manual-view"
      passLabels={filteredPassLabels}
      visibleColumns={visibleColumns}
      preheatUnit={resolvedPreheatUnit}
      selectable={false}
      filterApi={filterApi}
      renderRowActions={renderRowActions}
      renderPassCell={(pass) => renderPassWithIwmSwapBadge(pass, swapIwmBugType)}
      renderTorchCell={(pass) => {
        const row = byPass.get(pass)
        const torch: Torch = row?.torch ?? 'Lead'
        if (readOnly) {
          return (
            <span
              data-testid={`wp-torch-readonly-${pass}`}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                torch === 'Lead'
                  ? 'bg-accent-50 text-accent-700 ring-accent-200'
                  : 'bg-warning-50 text-warning-700 ring-warning-200'
              }`}
            >
              {torch}
            </span>
          )
        }
        return (
          <select
            data-testid={`wp-torch-${pass}`}
            className={`h-7 w-full min-w-[88px] max-w-[120px] rounded-md border px-2 text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              torch === 'Lead'
                ? 'border-accent-200 bg-accent-50 text-accent-700'
                : 'border-warning-200 bg-warning-50 text-warning-700'
            }`}
            value={torch}
            onChange={(event) => onTorchChange(pass, event.target.value as Torch)}
          >
            <option value="Lead">Lead</option>
            <option value="Trail">Trail</option>
          </select>
        )
      }}
      renderValueCell={(pass, col, agg) => {
        if (col.key === 'preheat') {
          const cells = resolvePreheatCellsForPass(
            preheatSection,
            pass,
            preheatInterpassByPass?.[pass]
          )
          const triple = applyPqrToleranceToTriple(
            {
              min: parsePreheatNumber(cells.min),
              avg: cells.avg,
              max: parsePreheatNumber(cells.max)
            },
            toleranceBoundary,
            pqrLimitsMode
          )
          const content =
            agg === 'avg'
              ? renderMatrixNumber(triple.avg)
              : renderMatrixNumber(agg === 'min' ? triple.min : triple.max)
          return (
            <span
              data-testid={`wp-cell-readonly-${pass}-${col.key}-${agg}`}
              className="inline-block min-h-[1.75rem] w-16 text-center"
              title="Edit on the Preheat page"
            >
              {content}
            </span>
          )
        }
        const row = byPass.get(pass)
        const stats = row?.values?.[col.key]
        const v = stats?.[agg]
        if (readOnly) {
          const triple = applyPqrToleranceToTriple(
            {
              min: stats?.min ?? null,
              avg: stats?.avg ?? null,
              max: stats?.max ?? null
            },
            toleranceBoundary,
            pqrLimitsMode
          )
          const display = formatInputValue(triple[agg])
          return (
            <span
              data-testid={`wp-cell-readonly-${pass}-${col.key}-${agg}`}
              className="inline-block min-h-[1.75rem] w-16 text-center text-[12px] font-semibold tabular-nums text-text-grey"
            >
              {display === '' ? <span className="text-[#C9CDD4]">—</span> : display}
            </span>
          )
        }
        return (
          <input
            type="number"
            data-testid={`wp-input-${pass}-${col.key}-${agg}`}
            className="h-7 w-16 rounded-md border border-[#E2E5EA] bg-white px-1.5 py-0 text-center text-[12px] tabular-nums text-text-grey transition-colors hover:border-[#C9CDD4] focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            value={formatInputValue(v)}
            onChange={(event) => {
              const text = event.target.value
              if (text === '') {
                onChange(pass, col.key, agg, null)
                return
              }
              const num = Number(text)
              onChange(pass, col.key, agg, Number.isFinite(num) ? num : null)
            }}
          />
        )
      }}
    />
  )
}

function indexRows(rows: ManualRow[]): Map<string, ManualRow> {
  const map = new Map<string, ManualRow>()
  for (const row of rows) {
    map.set(row.pass, row)
  }
  return map
}

function formatInputValue(value: number | null | undefined): string {
  return formatMatrixNumber(value)
}

function parsePreheatNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}
